import { AnyAction, combineReducers } from "redux";
import authReducer from "./reducers/auth.reducer";
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from "redux-persist";
import storage from "localforage";
import { configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import notifications from "./reducers/notifications.reducer";
import { queries } from "./reducers/queries.reducer";

const rootReducer = combineReducers({
	[authReducer.name]: persistReducer(
		{ key: authReducer.name, storage, blacklist: ["loading", "success", "error", "currentState"] },
		authReducer.reducer
	),
	[notifications.name]: persistReducer(
		{ key: notifications.name, storage, whitelist: ["showNotifications"] },
		notifications.reducer
	),
	[queries.reducerPath]: queries.reducer
});

type ReducerStateType = Parameters<typeof rootReducer>[0];

export const store = configureStore({
	reducer: (state: ReducerStateType, action: AnyAction) => {
		if (
			(action.error && action.error.message && action.error.message === "Invalid token.") ||
			(action.payload && action.payload.status === 401)
		) {
			const updatedState: ReducerStateType = state ? { ...state } : undefined;

			if (updatedState) {
				updatedState[authReducer.name] = {
					...updatedState[authReducer.name],
					loading: false,
					success: false,
					error: "",
					unauthorizedError: true,
					user: {
						...updatedState[authReducer.name].user,
						token: "invalid"
					}
				};
			}

			return rootReducer(updatedState, action);
		}

		return rootReducer(state, action);
	},

	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
			}
		}).concat(queries.middleware)
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
